import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {ReturnType<typeof setTimeout> | null} */
  reconcileTimer = null;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('cart-icon:cart-changed', this.reconcileCartCount);

    this.installCartFetchWatcher();
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('cart-icon:cart-changed', this.reconcileCartCount);
  }

  /**
   * Watches all cart network requests once, page-wide. Whenever anything
   * changes the cart (remove, add, update, clear), it tells every cart icon
   * to re-check the real count. This does not depend on theme events firing.
   */
  installCartFetchWatcher() {
    if (window.__cartIconFetchWatcher) return;
    window.__cartIconFetchWatcher = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = function (...args) {
      const response = originalFetch(...args);

      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) || '';

        if (/\/cart\/(change|add|update|clear)/.test(url)) {
          response
            .then(() => document.dispatchEvent(new CustomEvent('cart-icon:cart-changed')))
            .catch(() => {});
        }
      } catch (_) {
        // no-op
      }

      return response;
    };
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    // Optimistic update from the event payload so add-to-cart stays instant.
    this.renderCartBubble(itemCount, comingFromProductForm);

    // Then confirm against the live cart.
    this.reconcileCartCount();
  };

  /**
   * Confirms the bubble against the live cart and corrects it if it has drifted.
   * Debounced so a burst of updates only triggers one reconciliation.
   */
  reconcileCartCount = () => {
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);

    this.reconcileTimer = setTimeout(async () => {
      try {
        const cartUrl =
          (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) || '/cart';

        const response = await fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return;

        const cart = await response.json();

        // Set (not add) to the authoritative count, with no animation.
        this.renderCartBubble(cart.item_count, false, false);
      } catch (_) {
        // no-op
      }
    }, 150);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}